import React, { useState } from 'react';
import { Activity, Brain, Heart, Wind, Users, Shield, Database, ArrowRight, Zap, Menu, X, Microscope } from 'lucide-react';

const Home = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const NavBar = () => (
    <nav className="bg-black/80 backdrop-blur-xl text-white shadow-2xl sticky top-0 z-50 border-b border-cyan-500/20">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 group cursor-pointer">
            <div className="bg-gradient-to-br from-cyan-400 to-blue-500 p-2.5 rounded-xl shadow-lg shadow-cyan-500/50 group-hover:shadow-cyan-400/80 transition-all duration-300 transform group-hover:scale-110">
              <Activity className="h-7 w-7 text-black font-black" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tighter">AYUSHMAANAI</h1>
              <p className="text-xs text-cyan-400">Healthcare Intelligence</p>
            </div>
          </div>
          <div className="hidden md:flex space-x-8 items-center">
            {[
              { id: 'home', label: 'Home' },
              { id: 'analysis', label: 'Analysis' },
              { id: 'surge', label: 'Crisis Surge' },
              { id: 'agents', label: 'Agents' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`font-semibold transition-all duration-300 relative group pb-2 ${
                  activeTab === tab.id ? 'text-cyan-300' : 'text-gray-300 hover:text-cyan-300'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500"></div>
                )}
              </button>
            ))}
          </div>
          <button className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-cyan-400 to-blue-500 text-black rounded-full font-bold hover:shadow-lg hover:shadow-cyan-500/50 transition-all transform hover:scale-105">
            Get Started
          </button>
          <button className="md:hidden text-cyan-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 space-y-4 pb-4 border-t border-cyan-500/20 pt-4">
            {[
              { id: 'home', label: 'Home' },
              { id: 'analysis', label: 'Analysis' },
              { id: 'surge', label: 'Crisis Surge' },
              { id: 'agents', label: 'Agents' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                className="block w-full text-left text-cyan-300 hover:text-cyan-200 font-semibold"
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </nav>
  );

  const HomePage = () => (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      <div className="fixed inset-0 z-0">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-600/5 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600/5 rounded-full filter blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      <div className="container mx-auto px-6 py-32 relative z-10 min-h-screen flex flex-col justify-center">
        <div className="max-w-4xl mx-auto text-center space-y-12">
          <div className="space-y-6">
            <div className="inline-flex items-center justify-center space-x-2 px-4 py-2 bg-cyan-500/10 rounded-full border border-cyan-500/30">
              <Zap className="h-4 w-4 text-cyan-400" />
              <span className="text-sm font-semibold text-cyan-300">NEXT-GEN HEALTHCARE PLATFORM</span>
            </div>
            
            <h1 className="text-7xl md:text-8xl font-black leading-tight tracking-tighter">
              <span className="text-white">TRANSFORM</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-400 bg-clip-text text-transparent">PATIENT</span>
              <br />
              <span className="text-white">OUTCOMES</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Revolutionary AI-driven platform connecting healthcare providers with intelligent predictive analytics. Achieve unprecedented patient care excellence.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
            <button className="px-8 py-4 bg-gradient-to-r from-cyan-400 to-blue-500 text-black rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all transform hover:scale-105 flex items-center space-x-2 group">
              <span>Start Analyzing</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-2 transition-transform" />
            </button>
            <button className="px-8 py-4 bg-white/10 backdrop-blur-md text-cyan-300 border-2 border-cyan-500/50 rounded-full font-bold text-lg hover:bg-white/20 transition-all">
              View Demo
            </button>
          </div>

          <div className="grid grid-cols-3 gap-8 pt-16 border-t border-cyan-500/20">
            {[
              { value: '94.2%', label: 'Accuracy Rate' },
              { value: '2.5K+', label: 'Active Patients' },
              { value: '24/7', label: 'AI Monitoring' }
            ].map((stat, i) => (
              <div key={i} className="space-y-2">
                <p className="text-3xl md:text-4xl font-black bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">{stat.value}</p>
                <p className="text-gray-500 font-semibold">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-24 relative z-10">
        <h2 className="text-5xl font-black text-center mb-16">Core Capabilities</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: Database, title: 'RAG-Based Analysis', desc: 'LangChain-powered intelligent data processing' },
            { icon: Users, title: 'Smart Staffing', desc: 'AI-driven resource allocation and team efficiency' },
            { icon: Shield, title: 'Predictive Alerts', desc: 'Real-time warnings for health crises' }
          ].map((feature, idx) => (
            <div key={idx} className="group bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 hover:border-cyan-500/50 p-8 transition-all hover:shadow-2xl hover:shadow-cyan-500/20 hover:bg-white/10">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-6 group-hover:scale-125 transition-transform">
                <feature.icon className="h-7 w-7 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
              <p className="text-gray-400">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="container mx-auto px-6 py-24 relative z-10">
        <h2 className="text-5xl font-black text-center mb-16">Priority Populations</h2>
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { title: 'Children', icon: '👶', impact: 'Respiratory sensitivity' },
            { title: 'Newborns', icon: '🍼', impact: 'Immune development' },
            { title: 'Elderly', icon: '👴', impact: 'Chronic complications' },
            { title: 'Workers', icon: '💼', impact: 'Stress immunity' }
          ].map((group, idx) => (
            <div key={idx} className="group bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 hover:border-cyan-500/50 p-8 text-center transition-all hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/20">
              <div className="text-5xl mb-4">{group.icon}</div>
              <h3 className="text-xl font-bold mb-2">{group.title}</h3>
              <p className="text-gray-400 text-sm">{group.impact}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const AnalysisPage = () => {
    const [selectedOrgan, setSelectedOrgan] = useState('brain');
    
    return (
      <div className="min-h-screen bg-black text-white py-20">
        <div className="container mx-auto px-6">
          <div className="mb-16">
            <h1 className="text-6xl font-black mb-4">Medical Image Analysis</h1>
            <p className="text-xl text-gray-400">AI-powered diagnostic with precision anatomical mapping</p>
          </div>

          <div className="grid md:grid-cols-4 gap-4 mb-12">
            {[
              { id: 'brain', icon: Brain, label: 'Brain Tumor' },
              { id: 'lung', icon: Wind, label: 'Lung Analysis' },
              { id: 'heart', icon: Heart, label: 'Cardiac Scan' },
              { id: 'cancer', icon: Microscope, label: 'Cancer Detection' }
            ].map((organ) => (
              <button
                key={organ.id}
                onClick={() => setSelectedOrgan(organ.id)}
                className={`p-6 rounded-2xl font-bold transition-all duration-300 border ${
                  selectedOrgan === organ.id
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-black border-cyan-400 shadow-2xl shadow-cyan-500/50 scale-105'
                    : 'bg-white/5 text-gray-300 border-cyan-500/20 hover:border-cyan-500/50'
                }`}
              >
                <organ.icon className={`h-8 w-8 mx-auto mb-3 ${selectedOrgan === organ.id ? 'text-black' : 'text-cyan-400'}`} />
                {organ.label}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 shadow-2xl p-8">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-3xl font-bold">Brain MRI Scan</h2>
                <span className="px-4 py-2 bg-cyan-500/30 text-cyan-300 rounded-full text-sm font-semibold border border-cyan-500/50">Analyzing...</span>
              </div>
              <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl aspect-square flex items-center justify-center overflow-hidden border border-cyan-500/20">
                <div className="relative z-10 text-center">
                  <Brain className="h-48 w-48 text-cyan-400 mx-auto mb-4 animate-pulse" />
                  <p className="text-gray-300 text-lg">Brain scan loaded</p>
                  <p className="text-cyan-400/60 text-sm mt-2">Click to upload medical image</p>
                </div>
                <div className="absolute top-1/3 right-1/3 w-24 h-24 border-4 border-cyan-500 rounded-full animate-pulse"></div>
              </div>
              
              <div className="mt-8 grid grid-cols-3 gap-4">
                <button className="px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-black rounded-xl font-bold hover:shadow-lg hover:shadow-cyan-500/50 transition-all">Upload</button>
                <button className="px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-bold hover:shadow-lg hover:shadow-blue-500/50 transition-all">Analyze</button>
                <button className="px-4 py-3 bg-white/10 text-cyan-300 rounded-xl font-bold hover:bg-white/20 transition-all">Export</button>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 p-6">
                <h3 className="text-xl font-bold mb-6">Performance Metrics</h3>
                {[
                  { label: 'Accuracy', value: 94.2 },
                  { label: 'Sensitivity', value: 91.8 },
                  { label: 'Specificity', value: 96.5 },
                  { label: 'F1 Score', value: 93.1 }
                ].map((metric, i) => (
                  <div key={i} className="mb-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-400">{metric.label}</span>
                      <span className="text-cyan-400 font-bold">{metric.value}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full" style={{width: `${metric.value}%`}}></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 p-6">
                <h3 className="text-xl font-bold mb-4">Findings</h3>
                <div className="space-y-3">
                  <div className="p-3 bg-red-500/20 border-l-4 border-red-500 rounded">
                    <p className="font-semibold text-red-300">Abnormality Detected</p>
                    <p className="text-xs text-red-400">Right frontal - Grade II</p>
                  </div>
                  <div className="p-3 bg-yellow-500/20 border-l-4 border-yellow-500 rounded">
                    <p className="font-semibold text-yellow-300">Confidence</p>
                    <p className="text-xs text-yellow-400">High (89.3%)</p>
                  </div>
                  <div className="p-3 bg-cyan-500/20 border-l-4 border-cyan-500 rounded">
                    <p className="font-semibold text-cyan-300">Recommendation</p>
                    <p className="text-xs text-cyan-400">Urgent consultation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SurgeManagementPage = () => {
    const [selectedScenario, setSelectedScenario] = useState('diwali');

    const scenarios = {
      diwali: {
        name: 'Diwali Festival',
        icon: '🎆',
        severity: 'High',
        predictedSurge: 245,
        currentPatients: 120,
        surgePercentage: 104,
        timeline: '3-5 days',
        reasons: ['Air pollution spike', 'Respiratory issues', 'Burn injuries', 'Traffic accidents'],
        staffingRecommendation: {
          doctors: { current: 8, recommended: 15, increase: '+87%' },
          nurses: { current: 20, recommended: 40, increase: '+100%' },
          paramedics: { current: 12, recommended: 25, increase: '+108%' }
        },
        supplyRecommendation: [
          { item: 'Oxygen cylinders', current: 50, recommended: 150, unit: 'units', priority: 'Critical' },
          { item: 'Anti-inflammatory drugs', current: 500, recommended: 1500, unit: 'units', priority: 'Critical' },
          { item: 'Bandages & dressings', current: 1000, recommended: 3000, unit: 'units', priority: 'High' },
          { item: 'ICU beds', current: 5, recommended: 12, unit: 'beds', priority: 'Critical' }
        ],
        advisories: [
          { type: 'Alert', message: 'Respiratory patients should avoid outdoor activities during peak pollution hours' },
          { type: 'Alert', message: 'Increase staffing in Emergency and Respiratory departments by 100%' },
          { type: 'Info', message: 'Prepare burn treatment ward with additional resources' }
        ]
      },
      pollution: {
        name: 'Air Pollution Spike',
        icon: '🌫️',
        severity: 'Critical',
        predictedSurge: 380,
        currentPatients: 120,
        surgePercentage: 217,
        timeline: '1-2 days',
        reasons: ['AQI > 300', 'Respiratory distress', 'Asthma exacerbation', 'Cardiovascular issues'],
        staffingRecommendation: {
          doctors: { current: 8, recommended: 20, increase: '+150%' },
          nurses: { current: 20, recommended: 50, increase: '+150%' },
          paramedics: { current: 12, recommended: 30, increase: '+150%' }
        },
        supplyRecommendation: [
          { item: 'Oxygen cylinders', current: 50, recommended: 250, unit: 'units', priority: 'Critical' },
          { item: 'Ventilators', current: 3, recommended: 8, unit: 'units', priority: 'Critical' },
          { item: 'Inhaler medications', current: 200, recommended: 800, unit: 'units', priority: 'Critical' },
          { item: 'CPAP machines', current: 5, recommended: 15, unit: 'machines', priority: 'High' }
        ],
        advisories: [
          { type: 'Alert', message: 'CRITICAL: Activate emergency protocols for respiratory care' },
          { type: 'Alert', message: 'Triple staffing in Respiratory and ICU departments' },
          { type: 'Alert', message: 'Redirect non-emergency cases to partner hospitals' }
        ]
      },
      epidemic: {
        name: 'COVID-19 Wave',
        icon: '🦠',
        severity: 'Critical',
        predictedSurge: 520,
        currentPatients: 120,
        surgePercentage: 333,
        timeline: '2-3 weeks',
        reasons: ['New variant spread', 'Community transmission', 'Vulnerable population', 'Comorbidities'],
        staffingRecommendation: {
          doctors: { current: 8, recommended: 35, increase: '+337%' },
          nurses: { current: 20, recommended: 80, increase: '+300%' },
          paramedics: { current: 12, recommended: 50, increase: '+317%' }
        },
        supplyRecommendation: [
          { item: 'N95 masks', current: 500, recommended: 5000, unit: 'pieces', priority: 'Critical' },
          { item: 'PPE kits', current: 200, recommended: 2000, unit: 'kits', priority: 'Critical' },
          { item: 'Oxygen', current: 50, recommended: 400, unit: 'units', priority: 'Critical' },
          { item: 'Ventilators', current: 3, recommended: 20, unit: 'units', priority: 'Critical' }
        ],
        advisories: [
          { type: 'Alert', message: 'Activate isolation ward protocols immediately' },
          { type: 'Alert', message: 'Set up field hospital for overflow patients' },
          { type: 'Info', message: 'Coordinate with adjacent hospitals for patient distribution' }
        ]
      }
    };

    const current = scenarios[selectedScenario];

    return (
      <div className="min-h-screen bg-black text-white py-20">
        <div className="container mx-auto px-6">
          <div className="mb-16">
            <h1 className="text-6xl font-black mb-4">Crisis Surge Management</h1>
            <p className="text-xl text-gray-400">AI-powered predictive recommendations for patient surges</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {Object.entries(scenarios).map(([key, scenario]) => (
              <button
                key={key}
                onClick={() => setSelectedScenario(key)}
                className={`p-6 rounded-2xl font-bold transition-all duration-300 text-left border-2 ${
                  selectedScenario === key
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-black border-cyan-400 shadow-2xl shadow-cyan-500/50'
                    : 'bg-white/5 text-white border-cyan-500/20 hover:border-cyan-500/50'
                }`}
              >
                <div className="text-4xl mb-3">{scenario.icon}</div>
                <div className="text-lg">{scenario.name}</div>
                <div className={`text-sm mt-2 ${selectedScenario === key ? 'text-black/80' : 'text-gray-400'}`}>
                  {scenario.timeline}
                </div>
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 p-8">
              <h3 className="text-sm font-semibold text-gray-400 mb-2">PREDICTED SURGE</h3>
              <div className="text-5xl font-black text-cyan-400 mb-4">{current.predictedSurge}</div>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Current Patients</span>
                  <span className="text-white font-bold">{current.currentPatients}</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-yellow-500 to-red-500 h-2 rounded-full" 
                    style={{width: `${(current.currentPatients / current.predictedSurge) * 100}%`}}
                  ></div>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 p-8">
              <h3 className="text-sm font-semibold text-gray-400 mb-2">SURGE INCREASE</h3>
              <div className={`text-5xl font-black mb-4 ${current.surgePercentage > 200 ? 'text-red-400' : current.surgePercentage > 100 ? 'text-orange-400' : 'text-yellow-400'}`}>
                +{current.surgePercentage}%
              </div>
              <div className="text-sm text-gray-400">
                Expected within <span className="text-cyan-300 font-bold">{current.timeline}</span>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 p-8">
              <h3 className="text-sm font-semibold text-gray-400 mb-2">SEVERITY LEVEL</h3>
              <div className={`text-5xl font-black mb-4 ${current.severity === 'Critical' ? 'text-red-400' : 'text-orange-400'}`}>
                {current.severity}
              </div>
              <div className="text-sm text-gray-400">Immediate action required</div>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 p-8 mb-12">
            <h3 className="text-lg font-bold mb-6">Contributing Factors</h3>
            <div className="grid md:grid-cols-4 gap-4">
              {current.reasons.map((reason, i) => (
                <div key={i} className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 text-center">
                  <p className="text-gray-300 font-semibold">{reason}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 p-8 mb-12">
            <h3 className="text-lg font-bold mb-8">Staffing Recommendations</h3>
            <div className="grid md:grid-cols-3 gap-8">
              {Object.entries(current.staffingRecommendation).map(([role, data]) => (
                <div key={role} className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-xl p-6">
                  <p className="text-gray-400 text-sm font-semibold mb-4 uppercase">{role}</p>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-gray-300">Current</span>
                        <span className="text-lg font-bold text-cyan-400">{data.current}</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2">
                        <div className="bg-cyan-500 h-2 rounded-full" style={{width: '40%'}}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-gray-300">Recommended</span>
                        <span className="text-lg font-bold text-green-400">{data.recommended}</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{width: '100%'}}></div>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-cyan-500/20">
                      <p className="text-red-400 font-bold text-sm">{data.increase}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 p-8 mb-12">
            <h3 className="text-lg font-bold mb-8">Critical Supply Recommendations</h3>
            <div className="space-y-4">
              {current.supplyRecommendation.map((supply, i) => (
                <div key={i} className="bg-white/5 rounded-xl border border-cyan-500/20 hover:border-cyan-500/50 p-6 transition-all">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-bold text-lg">{supply.item}</h4>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          supply.priority === 'Critical' ? 'bg-red-500/30 text-red-300 border border-red-500/50' : 'bg-yellow-500/30 text-yellow-300 border border-yellow-500/50'
                        }`}>
                          {supply.priority}
                        </span>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-gray-400 mb-1">Current Stock</p>
                          <p className="text-xl font-bold text-cyan-400">{supply.current} {supply.unit}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">Recommended</p>
                          <p className="text-xl font-bold text-green-400">{supply.recommended} {supply.unit}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">Additional Needed</p>
                          <p className="text-xl font-bold text-orange-400">+{supply.recommended - supply.current} {supply.unit}</p>
                        </div>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2">
                        <div className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full" style={{width: `${(supply.current / supply.recommended) * 100}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 p-8">
            <h3 className="text-lg font-bold mb-8">Patient & Hospital Advisories</h3>
            <div className="space-y-4">
              {current.advisories.map((advisory, i) => (
                <div key={i} className="w-full text-left bg-gradient-to-r from-cyan-500/5 to-blue-500/5 border border-cyan-500/30 hover:border-cyan-500/60 rounded-xl p-6 transition-all">
                  <div className="flex items-center space-x-4">
                    <div className={`w-3 h-3 rounded-full ${advisory.type === 'Alert' ? 'bg-red-500' : 'bg-blue-500'}`}></div>
                    <div>
                      <p className={`font-bold ${advisory.type === 'Alert' ? 'text-red-300' : 'text-blue-300'}`}>
                        {advisory.type}
                      </p>
                      <p className="text-gray-300">{advisory.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AgentsPage = () => (
    <div className="min-h-screen bg-black text-white py-20">
      <div className="container mx-auto px-6">
        <h1 className="text-6xl font-black mb-4">Multi-Agent System</h1>
        <p className="text-xl text-gray-400 mb-16">LangChain RAG orchestration with specialized medical agents</p>

        <div className="mb-12 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 backdrop-blur-lg rounded-2xl border border-cyan-500/30 p-8 hover:border-cyan-500/60 transition-all">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold mb-2">Central Orchestrator</h2>
              <p className="text-gray-400">Coordinates all agents and manages intelligent data flow</p>
            </div>
            <Activity className="h-16 w-16 text-cyan-400 animate-pulse" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {[
            { icon: Brain, title: 'Neurological', desc: 'Brain scans, tumors, strokes, cognitive disorders', caps: ['Tumor detection', 'Stroke analysis', 'Dementia markers'] },
            { icon: Wind, title: 'Respiratory', desc: 'Lung health, pollution impact, diseases', caps: ['Pneumonia', 'COPD analysis', 'Pollution damage'] },
            { icon: Heart, title: 'Cardiac', desc: 'Heart conditions, arrhythmias, risks', caps: ['Arrhythmia', 'Heart failure', 'Blockage analysis'] },
            { icon: Microscope, title: 'Oncology', desc: 'Multi-organ cancer detection and tracking', caps: ['Cancer staging', 'Metastasis', 'Treatment response'] }
          ].map((agent, idx) => (
            <div key={idx} className="group bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 hover:border-cyan-500/50 p-8 transition-all hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/20">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/30 to-blue-500/30 flex items-center justify-center mb-6 group-hover:scale-125 transition-transform">
                <agent.icon className="h-8 w-8 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-bold mb-2">{agent.title} Agent</h3>
              <p className="text-gray-400 mb-6">{agent.desc}</p>
              <div className="space-y-2">
                {agent.caps.map((cap, i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                    <span className="text-sm text-gray-300">{cap}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-cyan-500/20 p-8">
          <h2 className="text-2xl font-bold mb-8">Agent Communication Flow</h2>
          <div className="space-y-4">
            {[
              'Patient data ingestion → LangChain RAG preprocessing',
              'Orchestrator routes to specialized agents based on symptoms',
              'Parallel analysis by Brain, Lung, Heart, and Cancer agents',
              'Agents return findings with anatomical highlights',
              'Orchestrator synthesizes results and generates recommendations',
              'Final report with staffing, supplies, and patient advisories'
            ].map((step, idx) => (
              <div key={idx} className="flex items-center space-x-4 p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/20 hover:border-cyan-500/50 transition-all">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-black flex items-center justify-center font-bold flex-shrink-0">
                  {idx + 1}
                </div>
                <p className="text-gray-300">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const Footer = () => (
    <footer className="bg-black border-t border-cyan-500/20 text-gray-400 py-16">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-gradient-to-br from-cyan-400 to-blue-500 p-2 rounded-lg">
                <Activity className="h-6 w-6 text-black" />
              </div>
              <span className="text-lg font-black text-white">AYUSHMAANAI</span>
            </div>
            <p className="text-sm">Revolutionizing healthcare with AI-powered predictive analytics.</p>
          </div>
          <div>
            <h4 className="font-bold text-white mb-4">Platform</h4>
            <ul className="space-y-2 text-sm">
              <li className="hover:text-cyan-400 cursor-pointer transition">Dashboard</li>
              <li className="hover:text-cyan-400 cursor-pointer transition">Tools</li>
              <li className="hover:text-cyan-400 cursor-pointer transition">Agents</li>
              <li className="hover:text-cyan-400 cursor-pointer transition">Reports</li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-white mb-4">Resources</h4>
            <ul className="space-y-2 text-sm">
              <li className="hover:text-cyan-400 cursor-pointer transition">Docs</li>
              <li className="hover:text-cyan-400 cursor-pointer transition">API</li>
              <li className="hover:text-cyan-400 cursor-pointer transition">Cases</li>
              <li className="hover:text-cyan-400 cursor-pointer transition">Support</li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-white mb-4">Contact</h4>
            <ul className="space-y-2 text-sm">
              <li>support@ayushmaanai.com</li>
              <li>+91-XXX-XXX-XXXX</li>
              <li className="text-cyan-400">24/7 Available</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-cyan-500/20 pt-8 flex justify-between items-center text-sm">
          <p>© 2025 AyushmaanAI. All rights reserved.</p>
          <div className="flex space-x-6">
            <span className="hover:text-cyan-400 cursor-pointer transition">Privacy</span>
            <span className="hover:text-cyan-400 cursor-pointer transition">Terms</span>
            <span className="hover:text-cyan-400 cursor-pointer transition">HIPAA</span>
          </div>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="min-h-screen bg-black">
      <NavBar />
      {activeTab === 'home' && <HomePage />}
      {activeTab === 'analysis' && <AnalysisPage />}
      {activeTab === 'surge' && <SurgeManagementPage />}
      {activeTab === 'agents' && <AgentsPage />}
      <Footer />
    </div>
  );
};

export default Home;